import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Popup from "../../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../../components/styles";
import {
    getAllExpenses,
    createExpense,
    approveExpense,
    rejectExpense,
    deleteExpense,
    getAllCategories,
    getExpensesForExport,
} from "../../../redux/expenseRelated/expenseHandle";
import { underExpenseControl } from "../../../redux/expenseRelated/expenseSlice";
import {
    categoryCodeFromCategory,
    categoryPayeeHint,
    categoryTitlePlaceholder,
    findCategoryById,
    isSalaryCategory,
} from "../../../constants/expenseCategories";
import { getAllTeachers } from "../../../redux/teacherRelated/teacherHandle";
import {
    currentSalaryPeriod,
    formatSalaryPeriodLabel,
    parseSalaryPeriod,
    recentSalaryPeriodOptions,
    resolveSalaryStatusForPeriod,
} from "../../../utils/salaryPeriod";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { expenseDetailPath } from "../../../utils/financePaths";

const TERMS = ["Term 1", "Term 2", "Term 3", "Full Year"];
const PAYMENT_METHODS = ["Cash", "Airtel Money", "TNM Mpamba", "Bank Transfer", "Cheque", "Other"];

const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

function fmtDDMMYYYY(d) {
    if (!d) return "—";
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return "—";
    const dd = String(x.getDate()).padStart(2, "0");
    const mm = String(x.getMonth() + 1).padStart(2, "0");
    const yyyy = x.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

function statusChipColor(status) {
    const s = String(status || "");
    if (s === "Pending") return "warning";
    if (s === "Approved") return "success";
    if (s === "Rejected") return "error";
    return "default";
}

function feeErrorMessage(err) {
    if (!err) return "Request failed";
    if (typeof err === "string") return err;
    if (typeof err.response?.data?.message === "string") return err.response.data.message;
    if (err.message) return err.message;
    return "Request failed";
}

function csvEscape(val) {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

const ExpenseLog = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useSelector((s) => s.user);
    const { expensesList, categoriesList, loading, response, error, statestatus } = useSelector((s) => s.expense);
    const { teachersList } = useSelector((s) => s.teacher);

    const schoolId = getSchoolIdForUser(currentUser);

    const [search, setSearch] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterTerm, setFilterTerm] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const filterRef = useRef({
        filterCategory: "",
        filterStatus: "",
        filterTerm: "",
        filterYear: "",
        dateFrom: "",
        dateTo: "",
    });
    filterRef.current = { filterCategory, filterStatus, filterTerm, filterYear, dateFrom, dateTo };

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [addOpen, setAddOpen] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [targetExpense, setTargetExpense] = useState(null);

    const [formTitle, setFormTitle] = useState("");
    const [formCategoryId, setFormCategoryId] = useState("");
    const [formAmount, setFormAmount] = useState("");
    const [formMethod, setFormMethod] = useState("Cash");
    const [formExpenseDate, setFormExpenseDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [formPayee, setFormPayee] = useState("");
    const [formReceiptRef, setFormReceiptRef] = useState("");
    const [formReferenceNumber, setFormReferenceNumber] = useState("");
    const [formTerm, setFormTerm] = useState("");
    const [formYear, setFormYear] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [formTeacherId, setFormTeacherId] = useState("");
    const [formSalaryPeriod, setFormSalaryPeriod] = useState(() => currentSalaryPeriod());

    const [rejectReason, setRejectReason] = useState("");

    const y = new Date().getFullYear();
    const ACADEMIC_YEARS = [`${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`];

    const rawList = useMemo(
        () => (Array.isArray(expensesList) ? expensesList : []),
        [expensesList]
    );

    const cats = useMemo(() => (Array.isArray(categoriesList) ? categoriesList : []), [categoriesList]);

    const selectedCategory = useMemo(
        () => findCategoryById(cats, formCategoryId),
        [cats, formCategoryId]
    );

    const defaultAcademicYear = ACADEMIC_YEARS[1] || ACADEMIC_YEARS[0] || "";

    const isSalary = isSalaryCategory(selectedCategory);

    const teachers = useMemo(() => (Array.isArray(teachersList) ? teachersList : []), [teachersList]);

    const salaryPeriodOptions = useMemo(() => recentSalaryPeriodOptions(12), []);

    const salaryEligibleTeachers = useMemo(
        () =>
            teachers.filter((t) => {
                const amt = Number(t.salaryAmount);
                if (!Number.isFinite(amt) || amt <= 0) return false;
                return resolveSalaryStatusForPeriod(t, formSalaryPeriod).status === "Unpaid";
            }),
        [teachers, formSalaryPeriod]
    );

    const selectedPayTeacher = useMemo(
        () => teachers.find((t) => (t._id || t.id) === formTeacherId) || null,
        [teachers, formTeacherId]
    );

    const loadExpenses = useCallback(() => {
        if (!schoolId) return;
        const {
            filterCategory: fc,
            filterStatus: fs,
            filterTerm: ft,
            filterYear: fy,
            dateFrom: df,
            dateTo: dt,
        } = filterRef.current;
        const filters = {};
        if (fc) filters.categoryId = fc;
        if (fs) filters.status = fs;
        if (ft) filters.term = ft;
        if (fy) filters.academicYear = fy;
        if (df) filters.from = df;
        if (dt) filters.to = dt;
        dispatch(getAllExpenses(schoolId, filters));
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (!schoolId) return;
        dispatch(getAllCategories(schoolId));
        dispatch(getAllTeachers(schoolId));
        loadExpenses();
    }, [dispatch, schoolId, loadExpenses]);

    useEffect(() => {
        if (!isSalary) {
            setFormTeacherId("");
            return;
        }
        setFormSalaryPeriod((prev) => parseSalaryPeriod(prev) || currentSalaryPeriod());
        if (!formTeacherId && salaryEligibleTeachers.length === 1) {
            const t = salaryEligibleTeachers[0];
            setFormTeacherId(t._id || t.id || "");
        }
    }, [isSalary, formTeacherId, salaryEligibleTeachers]);

    useEffect(() => {
        if (!isSalary) return;
        if (formTeacherId && !salaryEligibleTeachers.some((t) => (t._id || t.id) === formTeacherId)) {
            setFormTeacherId("");
        }
    }, [isSalary, formSalaryPeriod, formTeacherId, salaryEligibleTeachers]);

    useEffect(() => {
        if (!isSalary || !selectedPayTeacher) return;
        const amt = Number(selectedPayTeacher.salaryAmount);
        if (Number.isFinite(amt) && amt > 0) {
            setFormAmount(String(amt));
        }
        setFormPayee(String(selectedPayTeacher.name || "").trim());
        setFormTitle(
            `Monthly salary — ${selectedPayTeacher.name} — ${formatSalaryPeriodLabel(formSalaryPeriod)}`.trim()
        );
    }, [isSalary, selectedPayTeacher, formSalaryPeriod, formTerm, formYear]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage("Completed successfully!");
            setShowPopup(true);
            dispatch(underExpenseControl());
            loadExpenses();
            setAddOpen(false);
            setApproveOpen(false);
            setRejectOpen(false);
            setTargetExpense(null);
            setRejectReason("");
        } else if (statestatus === "failed") {
            const msg =
                typeof response === "string" && response
                    ? response
                    : feeErrorMessage(error);
            setMessage(msg || "Request failed");
            setShowPopup(true);
            dispatch(underExpenseControl());
        }
    }, [statestatus, response, error, dispatch, loadExpenses]);

    const filteredList = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rawList;
        return rawList.filter((e) => {
            const title = String(e.title || "").toLowerCase();
            const payee = String(e.payee || "").toLowerCase();
            return title.includes(q) || payee.includes(q);
        });
    }, [rawList, search]);

    const summaryStats = useMemo(() => {
        if (!rawList.length) return null;
        const now = new Date();
        const m = now.getMonth();
        const y = now.getFullYear();
        let approvedTotal = 0;
        let pendingCount = 0;
        let monthApproved = 0;
        const catIds = new Set();
        for (const e of rawList) {
            const amt = Number(e.amount) || 0;
            const st = e.status;
            if (st === "Approved") {
                approvedTotal += amt;
                catIds.add(e.categoryId || e.category?._id || e.category?.id);
                const d = new Date(e.expenseDate);
                if (!Number.isNaN(d.getTime()) && d.getMonth() === m && d.getFullYear() === y) {
                    monthApproved += amt;
                }
            }
            if (st === "Pending") pendingCount += 1;
        }
        return {
            approvedTotal,
            pendingCount,
            monthApproved,
            categoriesUsed: catIds.size,
        };
    }, [rawList]);

    const approvedTotalFiltered = useMemo(
        () => filteredList.filter((e) => e.status === "Approved").reduce((s, e) => s + (Number(e.amount) || 0), 0),
        [filteredList]
    );

    const clearFilters = () => {
        setSearch("");
        setFilterCategory("");
        setFilterStatus("");
        setFilterTerm("");
        setFilterYear("");
        setDateFrom("");
        setDateTo("");
        if (schoolId) {
            dispatch(getAllExpenses(schoolId, {}));
        }
    };

    const openAdd = () => {
        const firstCat = cats[0];
        const firstId = firstCat ? firstCat._id || firstCat.id : "";
        setFormTitle("");
        setFormCategoryId(firstId);
        setFormAmount("");
        setFormMethod("Cash");
        setFormExpenseDate(new Date().toISOString().slice(0, 10));
        setFormPayee("");
        setFormReceiptRef("");
        setFormReferenceNumber("");
        setFormTerm("Term 1");
        setFormYear(defaultAcademicYear);
        setFormDescription("");
        setFormTeacherId("");
        setFormSalaryPeriod(currentSalaryPeriod());
        setAddOpen(true);
    };

    useEffect(() => {
        if (!addOpen || !cats.length || formCategoryId) return;
        const first = cats[0];
        setFormCategoryId(first._id || first.id || "");
    }, [addOpen, cats, formCategoryId]);

    const submitAdd = () => {
        if (!schoolId) return;
        const amt = Number(formAmount);
        if (!String(formTitle || "").trim() || !formCategoryId || !Number.isFinite(amt) || amt <= 0) return;
        if (isSalary && !formTeacherId) return;
        const payload = {
            schoolId,
            categoryId: formCategoryId,
            title: String(formTitle).trim(),
            description: String(formDescription || "").trim(),
            amount: amt,
            expenseDate: formExpenseDate,
            payee: String(formPayee || "").trim(),
            receiptRef: String(formReceiptRef || "").trim(),
            paymentMethod: formMethod,
            referenceNumber: String(formReferenceNumber || "").trim(),
            term: String(formTerm || "").trim(),
            academicYear: String(formYear || "").trim(),
            notes: "",
            submittedById: currentUser._id,
        };
        if (isSalary) {
            payload.teacherId = formTeacherId;
            payload.salaryPeriod = formSalaryPeriod;
        }
        dispatch(createExpense(payload));
    };

    const openApprove = (e) => {
        setTargetExpense(e);
        setApproveOpen(true);
    };

    const confirmApprove = () => {
        const id = targetExpense?._id || targetExpense?.id;
        if (!id || !currentUser?._id) return;
        dispatch(approveExpense(id, { approvedById: currentUser._id }));
    };

    const openReject = (e) => {
        setTargetExpense(e);
        setRejectReason("");
        setRejectOpen(true);
    };

    const confirmReject = () => {
        const id = targetExpense?._id || targetExpense?.id;
        if (!id || !currentUser?._id || !String(rejectReason || "").trim()) return;
        dispatch(
            rejectExpense(id, {
                rejectedReason: String(rejectReason).trim(),
                approvedById: currentUser._id,
            })
        );
    };

    const handleDelete = (e) => {
        const id = e._id || e.id;
        if (!id) return;
        if (!window.confirm("Delete this expense? This cannot be undone.")) return;
        dispatch(deleteExpense(id));
    };

    const buildActiveFilters = () => {
        const filters = {};
        if (filterCategory) filters.categoryId = filterCategory;
        if (filterStatus) filters.status = filterStatus;
        if (filterTerm) filters.term = filterTerm;
        if (filterYear) filters.academicYear = filterYear;
        if (dateFrom) filters.from = dateFrom;
        if (dateTo) filters.to = dateTo;
        return filters;
    };

    const handleExportCsv = async () => {
        if (!schoolId) return;
        const data = await dispatch(getExpensesForExport(schoolId, buildActiveFilters()));
        if (!data || !Array.isArray(data)) {
            setMessage("Could not export expenses.");
            setShowPopup(true);
            return;
        }
        const headers = [
            "Date",
            "Category",
            "Title",
            "Payee",
            "Receipt Ref",
            "Payment Method",
            "Reference",
            "Amount (MWK)",
            "Status",
            "Term",
            "Year",
            "Notes",
        ];
        const rows = data.map((row) =>
            [
                csvEscape(row.date),
                csvEscape(row.category),
                csvEscape(row.title),
                csvEscape(row.payee),
                csvEscape(row.receiptRef),
                csvEscape(row.method),
                csvEscape(row.reference),
                csvEscape(row.amount),
                csvEscape(row.status),
                csvEscape(row.term),
                csvEscape(row.year),
                csvEscape(row.notes),
            ].join(",")
        );
        const csv = [headers.join(","), ...rows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const day = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `expenses-${day}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const rowId = (e) => e._id || e.id;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5" component="h1" fontWeight={600}>
                        School expenses
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Salaries, materials, maintenance, trips, awards, and other spending
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv} disabled={!schoolId}>
                        Export CSV
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={!schoolId || loading}>
                        Record expense
                    </Button>
                </Stack>
            </Stack>

            {cats.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {cats.map((c) => {
                        const cid = c._id || c.id;
                        return (
                            <Chip
                                key={cid}
                                label={c.name}
                                size="small"
                                variant="outlined"
                                title={c.description || undefined}
                            />
                        );
                    })}
                </Stack>
            )}

            {summaryStats && rawList.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                            <Typography variant="h6" color="success.main" fontWeight={700}>
                                {formatMWK(summaryStats.approvedTotal)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Total Approved
                            </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography variant="h6" color="warning.main" fontWeight={700}>
                                {summaryStats.pendingCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Pending Approval
                            </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography variant="h6" color="success.main" fontWeight={700}>
                                {formatMWK(summaryStats.monthApproved)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                This Month
                            </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Typography variant="h6" color="text.primary" fontWeight={700}>
                                {summaryStats.categoriesUsed}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Categories Used
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search"
                            placeholder="Title or payee"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                label="Category"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Categories</em>
                                </MenuItem>
                                {cats.map((c) => {
                                    const cid = c._id || c.id;
                                    return (
                                        <MenuItem key={cid} value={cid}>
                                            {c.name}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Status</em>
                                </MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Term</InputLabel>
                            <Select label="Term" value={filterTerm} onChange={(e) => setFilterTerm(e.target.value)}>
                                <MenuItem value="">
                                    <em>All Terms</em>
                                </MenuItem>
                                {TERMS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Academic year</InputLabel>
                            <Select
                                label="Academic year"
                                value={filterYear}
                                onChange={(e) => setFilterYear(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All years</em>
                                </MenuItem>
                                {ACADEMIC_YEARS.map((yr) => (
                                    <MenuItem key={yr} value={yr}>
                                        {yr}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Date from"
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Date to"
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                        <Button fullWidth variant="contained" onClick={loadExpenses} disabled={!schoolId}>
                            Apply
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                        <Button fullWidth variant="text" onClick={clearFilters} disabled={!schoolId}>
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Date</StyledTableCell>
                            <StyledTableCell>Category</StyledTableCell>
                            <StyledTableCell>Title</StyledTableCell>
                            <StyledTableCell>Payee</StyledTableCell>
                            <StyledTableCell>Ref</StyledTableCell>
                            <StyledTableCell>Method</StyledTableCell>
                            <StyledTableCell align="right">Amount</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                            <StyledTableCell align="right">Actions</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && filteredList.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={9} align="center">
                                    Loading…
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : filteredList.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={9} align="center">
                                    No expenses match your filters.
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : (
                            filteredList.map((e) => {
                                const id = rowId(e);
                                const st = e.status;
                                return (
                                    <StyledTableRow key={id}>
                                        <StyledTableCell>{fmtDDMMYYYY(e.expenseDate)}</StyledTableCell>
                                        <StyledTableCell>{e.category?.name ?? "—"}</StyledTableCell>
                                        <StyledTableCell>{e.title ?? "—"}</StyledTableCell>
                                        <StyledTableCell>{e.payee || "—"}</StyledTableCell>
                                        <StyledTableCell>{e.receiptRef || "—"}</StyledTableCell>
                                        <StyledTableCell>{e.paymentMethod || "—"}</StyledTableCell>
                                        <StyledTableCell align="right" sx={{ fontWeight: 700 }}>
                                            {formatMWK(e.amount)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Chip
                                                size="small"
                                                label={st || "—"}
                                                color={statusChipColor(st)}
                                                variant="outlined"
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                            <Tooltip title="View">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(expenseDetailPath(location.pathname, id))}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {st === "Pending" && (
                                                <>
                                                    <Tooltip title="Approve">
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: "success.main" }}
                                                            onClick={() => openApprove(e)}
                                                        >
                                                            <CheckCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reject">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => openReject(e)}
                                                        >
                                                            <CancelOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {st !== "Approved" && (
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(e)}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })
                        )}
                    </TableBody>
                    {filteredList.length > 0 && (
                        <TableFooter
                            sx={{
                                position: "sticky",
                                bottom: 0,
                                bgcolor: "background.paper",
                                zIndex: 2,
                                boxShadow: (theme) => theme.shadows[4],
                            }}
                        >
                            <TableRow>
                                <StyledTableCell colSpan={9} align="right" sx={{ fontWeight: 700, borderTop: 2 }}>
                                    <Typography component="span" variant="body2" color="success.main" fontWeight={700}>
                                        Approved total: {formatMWK(approvedTotalFiltered)}
                                    </Typography>
                                </StyledTableCell>
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Record school expense</DialogTitle>
                <DialogContent dividers>
                    {cats.length === 0 ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Expense categories are loading. Close and try again, or refresh the page.
                        </Alert>
                    ) : null}
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required size="small">
                                <InputLabel>Expense category</InputLabel>
                                <Select
                                    label="Expense category"
                                    value={formCategoryId}
                                    onChange={(e) => setFormCategoryId(e.target.value)}
                                >
                                    {cats.map((c) => {
                                        const cid = c._id || c.id;
                                        return (
                                            <MenuItem key={cid} value={cid}>
                                                <Box>
                                                    <Typography variant="body2">{c.name}</Typography>
                                                    {c.description ? (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {c.description}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                            {selectedCategory?.description ? (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                                    {selectedCategory.description}
                                </Typography>
                            ) : null}
                        </Grid>
                        {isSalary ? (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required size="small">
                                    <InputLabel>Salary month</InputLabel>
                                    <Select
                                        label="Salary month"
                                        value={formSalaryPeriod}
                                        onChange={(e) => setFormSalaryPeriod(e.target.value)}
                                    >
                                        {salaryPeriodOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        ) : null}
                        {isSalary ? (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required size="small">
                                    <InputLabel>Teacher (salary recipient)</InputLabel>
                                    <Select
                                        label="Teacher (salary recipient)"
                                        value={formTeacherId}
                                        onChange={(e) => setFormTeacherId(e.target.value)}
                                    >
                                        {salaryEligibleTeachers.length === 0 ? (
                                            <MenuItem value="" disabled>
                                                No teachers with unpaid salary on file
                                            </MenuItem>
                                        ) : (
                                            salaryEligibleTeachers.map((t) => {
                                                const tid = t._id || t.id;
                                                return (
                                                    <MenuItem key={tid} value={tid}>
                                                        {t.name} — {formatMWK(t.salaryAmount)}
                                                    </MenuItem>
                                                );
                                            })
                                        )}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                                    Monthly salary is paid in full for the selected month. Approve the expense to mark the teacher
                                    as paid for {formatSalaryPeriodLabel(formSalaryPeriod)} on their portal.
                                </Typography>
                            </Grid>
                        ) : null}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                label="What was this expense for?"
                                placeholder={categoryTitlePlaceholder(categoryCodeFromCategory(selectedCategory))}
                                value={formTitle}
                                onChange={(e) => setFormTitle(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                label="Amount"
                                type="number"
                                value={formAmount}
                                onChange={(e) => !isSalary && setFormAmount(e.target.value)}
                                inputProps={{ min: 0, step: "0.01", readOnly: isSalary }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">MWK</InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Payment method</InputLabel>
                                <Select
                                    label="Payment method"
                                    value={formMethod}
                                    onChange={(e) => setFormMethod(e.target.value)}
                                >
                                    {PAYMENT_METHODS.map((m) => (
                                        <MenuItem key={m} value={m}>
                                            {m}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                label="Expense date"
                                type="date"
                                value={formExpenseDate}
                                onChange={(e) => setFormExpenseDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Paid to (payee)"
                                value={formPayee}
                                onChange={(e) => setFormPayee(e.target.value)}
                                helperText={categoryPayeeHint(categoryCodeFromCategory(selectedCategory))}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Receipt / Invoice ref"
                                value={formReceiptRef}
                                onChange={(e) => setFormReceiptRef(e.target.value)}
                                helperText="Physical receipt or invoice number"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Reference number"
                                value={formReferenceNumber}
                                onChange={(e) => setFormReferenceNumber(e.target.value)}
                                helperText="Bank or mobile money transaction ref"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Term</InputLabel>
                                <Select label="Term" value={formTerm} onChange={(e) => setFormTerm(e.target.value)}>
                                    {TERMS.map((t) => (
                                        <MenuItem key={t} value={t}>
                                            {t}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Academic year</InputLabel>
                                <Select label="Academic year" value={formYear} onChange={(e) => setFormYear(e.target.value)}>
                                    {ACADEMIC_YEARS.map((yr) => (
                                        <MenuItem key={yr} value={yr}>
                                            {yr}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Additional details"
                                value={formDescription}
                                onChange={(e) => setFormDescription(e.target.value)}
                                multiline
                                minRows={2}
                                placeholder="Line items, invoice notes, or internal remarks"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={submitAdd}
                        disabled={
                            loading ||
                            !String(formTitle || "").trim() ||
                            !formCategoryId ||
                            !Number(formAmount) ||
                            Number(formAmount) <= 0 ||
                            (isSalary && !formTeacherId)
                        }
                    >
                        Save Expense
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Approve expense</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Alert severity="info">
                            <strong>{targetExpense?.title}</strong>
                            <br />
                            Amount: {formatMWK(targetExpense?.amount)}
                        </Alert>
                        <Typography variant="body2">Are you sure you want to approve this expense?</Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={confirmApprove} disabled={loading}>
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Reject expense</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Alert severity="warning">
                            Rejecting this expense removes it from approved spend.
                        </Alert>
                        <TextField
                            required
                            fullWidth
                            multiline
                            minRows={3}
                            label="Reason for rejection"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmReject}
                        disabled={loading || !String(rejectReason || "").trim()}
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ExpenseLog;
